// src/pages/Blog.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import LazyImage from '../components/LazyImage';
import '../Blog.css';

// ── Cover fallback helpers ────────────────────────────────────────────────────

const CATEGORY_CONFIG = {
  'Cost Management':    { icon: 'fa-dollar-sign',      gradients: ['#667eea, #764ba2', '#f093fb, #f5576c', '#4facfe, #00f2fe'] },
  'Benefits Strategy':  { icon: 'fa-chess',             gradients: ['#43e97b, #38f9d7', '#fa709a, #fee140', '#a18cd1, #fbc2eb'] },
  'Industry Trends':    { icon: 'fa-chart-line',        gradients: ['#f77062, #fe5196', '#4facfe, #00f2fe', '#43e97b, #38f9d7'] },
  'Data Analytics':     { icon: 'fa-database',          gradients: ['#4facfe, #00f2fe', '#667eea, #764ba2', '#f093fb, #f5576c'] },
  'Compliance':         { icon: 'fa-shield-halved',     gradients: ['#a18cd1, #fbc2eb', '#ffecd2, #fcb69f', '#667eea, #764ba2'] },
  'Wellness':           { icon: 'fa-heart-pulse',       gradients: ['#43e97b, #38f9d7', '#fa709a, #fee140', '#f093fb, #f5576c'] },
  'Technology':         { icon: 'fa-microchip',         gradients: ['#4facfe, #00f2fe', '#667eea, #764ba2', '#43e97b, #38f9d7'] },
  'Case Studies':       { icon: 'fa-file-medical',      gradients: ['#fa709a, #fee140', '#f77062, #fe5196', '#a18cd1, #fbc2eb'] },
  default:              { icon: 'fa-hospital',          gradients: ['#667eea, #764ba2', '#4facfe, #00f2fe', '#43e97b, #38f9d7'] },
};

function getCoverConfig(category, id) {
  const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.default;
  // Use post id to pick a consistent gradient (not random on re-render)
  const idx = id ? id.charCodeAt(0) % cfg.gradients.length : 0;
  return { icon: cfg.icon, gradient: cfg.gradients[idx] };
}

const CoverFallback = ({ category, postId, className = '' }) => {
  const { icon, gradient } = getCoverConfig(category, postId);
  return (
    <div
      className={`cover-fallback ${className}`}
      style={{ background: `linear-gradient(135deg, ${gradient})` }}
    >
      <i className={`fa-solid ${icon}`} />
    </div>
  );
};

const CoverImage = ({ src, alt, category, postId, className = '' }) => {
  const [broken, setBroken] = useState(!src);
  if (broken) return <CoverFallback category={category} postId={postId} className={className} />;
  return (
    <LazyImage
      src={src}
      alt={alt}
      onError={() => setBroken(true)}
    />
  );
};

// ── Blog page ─────────────────────────────────────────────────────────────────

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const { isWriter } = useAuth();

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
      // A stalled Firestore stream neither resolves nor rejects, so bound it
      // ourselves: an error state is recoverable, an endless spinner is not.
      const snapshot = await Promise.race([
        getDocs(q),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timed out reaching Firestore')), 12000)
        ),
      ]);
      const postsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (postsData.length > 0) {
        setFeaturedPost(postsData[0]);
        setPosts(postsData.slice(1));
      }
      setLoadError(null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setLoadError(error);
      setLoading(false);
    }
  };

  const retry = () => {
    setLoadError(null);
    setLoading(true);
    fetchPosts();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const calculateReadTime = (content) => {
    const text = content.replace(/<[^>]*>/g, '');
    const wordCount = text.split(/\s+/).length;
    return `${Math.ceil(wordCount / 200)} min read`;
  };

  const getExcerpt = (content, length = 150) => {
    const text = content.replace(/<[^>]*>/g, '');
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="blog-container">
          <div className="blog-loading">
            <div className="loading-spinner"></div>
            <p>Loading articles...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-content">
        <div className="blog-container">
          <div className="blog-loading">
            <p>We could not load the articles just now.</p>
            <button type="button" className="btn btn-primary" onClick={retry}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="blog-container">
        {/* Blog Header */}
        <header className="blog-header">
          <h1 className="blog-main-title">
            Avenir <span className="gradient-text">Insights</span>
          </h1>
          <p className="blog-subtitle">
            Thoughts on employee benefits, self-insurance, and the future of healthcare
          </p>
          {isWriter && (
            <Link to="/blog/new" className="btn btn-primary write-btn-header">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Write Article
            </Link>
          )}
        </header>

        {/* No Posts State */}
        {!featuredPost && posts.length === 0 && (
          <div className="blog-empty">
            <div className="empty-icon">📝</div>
            <h2>No articles yet</h2>
            <p>Check back soon for insights on employee benefits and healthcare.</p>
            {isWriter && (
              <Link to="/blog/new" className="btn btn-primary">Write the First Article</Link>
            )}
          </div>
        )}

        {/* Featured Post */}
        {featuredPost && (
          <section className="featured-section">
            <Link to={`/blog/${featuredPost.id}`} className="featured-post">
              <div className="featured-image">
                <CoverImage
                  src={featuredPost.coverImage}
                  alt={featuredPost.title}
                  category={featuredPost.category}
                  postId={featuredPost.id}
                />
              </div>
              <div className="featured-content">
                <span className="featured-badge">Featured</span>
                {featuredPost.category && (
                  <span className="post-category">{featuredPost.category}</span>
                )}
                <h2 className="featured-title">{featuredPost.title}</h2>
                {featuredPost.subtitle && (
                  <p className="featured-subtitle">{featuredPost.subtitle}</p>
                )}
                <p className="featured-excerpt">{getExcerpt(featuredPost.content, 200)}</p>
                <div className="post-meta">
                  <div className="author-info">
                    <div className="author-avatar">{featuredPost.authorName?.charAt(0) || 'A'}</div>
                    <span className="author-name">{featuredPost.authorName || 'Avenir Team'}</span>
                  </div>
                  <span className="meta-separator">·</span>
                  <span className="post-date">{formatDate(featuredPost.createdAt)}</span>
                  <span className="meta-separator">·</span>
                  <span className="read-time">{calculateReadTime(featuredPost.content)}</span>
                </div>
              </div>
            </Link>
          </section>
        )}

        {/* Posts Grid */}
        {posts.length > 0 && (
          <section className="posts-section">
            <h3 className="section-label">Latest Articles</h3>
            <div className="posts-grid">
              {posts.map(post => (
                <Link to={`/blog/${post.id}`} key={post.id} className="post-card">
                  <div className="post-card-image">
                    <CoverImage
                      src={post.coverImage}
                      alt={post.title}
                      category={post.category}
                      postId={post.id}
                    />
                  </div>
                  <div className="post-card-content">
                    {post.category && (
                      <span className="post-category">{post.category}</span>
                    )}
                    <h3 className="post-card-title">{post.title}</h3>
                    {post.subtitle && (
                      <p className="post-card-subtitle">{post.subtitle}</p>
                    )}
                    <p className="post-card-excerpt">{getExcerpt(post.content)}</p>
                    <div className="post-meta">
                      <div className="author-info">
                        <div className="author-avatar small">{post.authorName?.charAt(0) || 'A'}</div>
                        <span className="author-name">{post.authorName || 'Avenir Team'}</span>
                      </div>
                      <span className="meta-separator">·</span>
                      <span className="post-date">{formatDate(post.createdAt)}</span>
                      <span className="meta-separator">·</span>
                      <span className="read-time">{calculateReadTime(post.content)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Blog;